import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Check, 
  Mail, 
  ArrowLeft,
  Sun,
  Moon,
  CreditCard,
  Shield,
  Loader2,
  Building2,
  Info,
  Minus
} from "lucide-react";
import { useTheme, useAuth, API } from "../App";
import { apiFetch } from "../lib/apiFetch";
import { toast } from "sonner";

const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [testMode, setTestMode] = useState(false);
  const [testCardNumber, setTestCardNumber] = useState("");

  useEffect(() => {
    // Detect Stripe test mode to display a QA-friendly hint with the test card number.
    (async () => {
      try {
        const r = await apiFetch(`${API}/billing/config`);
        if (r.ok) {
          const d = await r.json();
          setTestMode(!!d.test_mode);
          setTestCardNumber(d.test_card_number || "");
        }
      } catch (_) { /* silent */ }
    })();
  }, []);
  
  // Language from URL param or default to FR
  const lang = searchParams.get("lang") || "fr";
  const isFr = lang === "fr";

  const content = {
    fr: {
      back: "Retour à l'accueil",
      badge: "Tarification",
      title: "Choisissez votre plan",
      subtitle: "14 jours d'essai gratuit sur tous les plans, sans carte bancaire.",
      trial: "14 jours d'essai gratuit",
      perMonth: "/mois",
      cta: "Démarrer l'essai",
      ctaLoading: "Redirection...",
      popular: "Populaire",
      cardNote: "Sans carte bancaire pendant l'essai",
      securePayment: "Paiement sécurisé par Stripe",
      login: "Connexion",
      dashboard: "Dashboard",
      unlimited: "illimité",
      faqTitle: "Questions fréquentes",
      testimonialsTitle: "Ils ont choisi ALIA",
      testimonialsSubtitle: "Des équipes qui ont repris le contrôle de leur flux email.",
      testimonials: [
        {
          quote: "ALIA m'a permis de répondre 3x plus vite aux demandes commerciales urgentes. Sur le dernier trimestre, j'estime que mon équipe a regagné 18h/semaine sur le traitement des leads entrants.",
          name: "Camille Lefebvre",
          role: "Directrice Commerciale",
          company: "Groupe Aximys",
          plan: "Pro"
        },
        {
          quote: "En cabinet, chaque email contient un enjeu juridique. La catégorisation ALIA me distingue immédiatement les urgences contentieuses des relances administratives. Je sors 2 heures plus tôt chaque soir.",
          name: "Maître Antoine Berthier",
          role: "Avocat Associé",
          company: "Berthier & Associés",
          plan: "Starter"
        },
        {
          quote: "Sur mes projets multi-équipes, ALIA délègue automatiquement les emails vers les bons interlocuteurs. Le suivi est centralisé, plus rien ne tombe entre les chaises. ROI atteint en 5 semaines.",
          name: "Yacine Moreau",
          role: "Chef de Projet Senior",
          company: "Innovatech Industries",
          plan: "Business"
        }
      ],
      comparatorTitle: "Comparez les plans en détail",
      comparatorFeatures: [
        { label: "Comptes email", values: ["1", "5", "Illimité"] },
        { label: "Catégorisation IA", values: [true, true, true] },
        { label: "Suggestions de réponses", values: [true, true, true] },
        { label: "Actions / mois", values: ["2 000", "10 000", "Illimité"] },
        { label: "Automatisation follow-up", values: [false, true, true] },
        { label: "Création de tâches", values: [false, true, true] },
        { label: "Collaboration équipe", values: [false, false, true] },
        { label: "Automatisation avancée", values: [false, false, true] },
        { label: "Accès API", values: [false, false, true] },
        { label: "Support", values: ["Standard", "Prioritaire", "Dédié 24/7"] }
      ],
      faq: [
        {
          q: "Que se passe-t-il après les 14 jours d'essai ?",
          a: "Votre moyen de paiement sera débité du montant de votre plan après la période d'essai. Vous pouvez annuler à tout moment."
        },
        {
          q: "Puis-je changer de plan ?",
          a: "Oui, vous pouvez upgrader ou downgrader votre plan à tout moment depuis votre tableau de bord."
        },
        {
          q: "Quels moyens de paiement acceptez-vous ?",
          a: "Nous acceptons les cartes bancaires (Visa, Mastercard, Amex) et les prélèvements SEPA (IBAN)."
        }
      ]
    },
    en: {
      back: "Back to home",
      badge: "Pricing",
      title: "Choose your plan",
      subtitle: "14-day free trial on all plans, no credit card required.",
      trial: "14-day free trial",
      perMonth: "/month",
      cta: "Start trial",
      ctaLoading: "Redirecting...",
      popular: "Popular",
      cardNote: "No credit card required during trial",
      securePayment: "Secure payment by Stripe",
      login: "Login",
      dashboard: "Dashboard",
      unlimited: "unlimited",
      faqTitle: "Frequently asked questions",
      testimonialsTitle: "They chose ALIA",
      testimonialsSubtitle: "Teams that took back control of their email flow.",
      testimonials: [
        {
          quote: "ALIA helps me respond 3x faster to urgent sales requests. Last quarter, my team recovered about 18h/week on incoming lead processing.",
          name: "Camille Lefebvre",
          role: "Sales Director",
          company: "Aximys Group",
          plan: "Pro"
        },
        {
          quote: "In a law firm, every email carries legal stakes. ALIA categorization distinguishes urgent litigation from admin follow-ups instantly. I leave the office 2 hours earlier every day.",
          name: "Antoine Berthier, Esq.",
          role: "Partner Attorney",
          company: "Berthier & Partners",
          plan: "Starter"
        },
        {
          quote: "On my cross-team projects, ALIA automatically delegates emails to the right people. Tracking is centralized, nothing falls through the cracks. ROI in 5 weeks.",
          name: "Yacine Moreau",
          role: "Senior Project Manager",
          company: "Innovatech Industries",
          plan: "Business"
        }
      ],
      comparatorTitle: "Compare plans in detail",
      comparatorFeatures: [
        { label: "Email accounts", values: ["1", "5", "Unlimited"] },
        { label: "AI categorization", values: [true, true, true] },
        { label: "Response suggestions", values: [true, true, true] },
        { label: "Actions / month", values: ["2,000", "10,000", "Unlimited"] },
        { label: "Follow-up automation", values: [false, true, true] },
        { label: "Task creation", values: [false, true, true] },
        { label: "Team collaboration", values: [false, false, true] },
        { label: "Advanced automation", values: [false, false, true] },
        { label: "API access", values: [false, false, true] },
        { label: "Support", values: ["Standard", "Priority", "Dedicated 24/7"] }
      ],
      faq: [
        {
          q: "What happens after the 14-day trial?",
          a: "Your payment method will be charged after the trial period. You can cancel anytime."
        },
        {
          q: "Can I change plans?",
          a: "Yes, you can upgrade or downgrade your plan anytime from your dashboard."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept credit cards (Visa, Mastercard, Amex) and SEPA direct debit (IBAN)."
        }
      ]
    }
  };

  const t = content[isFr ? "fr" : "en"];

  const plans = [
    {
      id: "starter",
      name: "Starter",
      price: 39,
      features: isFr 
        ? ["1 compte email", "Catégorisation IA", "Suggestions de réponses", "2 000 actions/mois"]
        : ["1 email account", "AI categorization", "Response suggestions", "2,000 actions/month"],
      popular: false
    },
    {
      id: "pro",
      name: "Pro",
      price: 99,
      features: isFr
        ? ["5 comptes email", "Automatisation follow-up", "Création de tâches", "10 000 actions/mois", "Support prioritaire"]
        : ["5 email accounts", "Follow-up automation", "Task creation", "10,000 actions/month", "Priority support"],
      popular: true
    },
    {
      id: "business",
      name: "Business",
      price: 250,
      features: isFr
        ? ["Comptes illimités", "Automatisation avancée", "Collaboration équipe", "Actions illimitées", "Support dédié", "Accès API"]
        : ["Unlimited accounts", "Advanced automation", "Team collaboration", "Unlimited actions", "Dedicated support", "API access"],
      popular: false
    }
  ];

  const handleStartTrial = async (planId) => {
    if (!user) {
      // Preserve the plan intent so the user is sent to Stripe right after signup
      navigate(`/register?plan=${encodeURIComponent(planId)}`);
      return;
    }

    setLoadingPlan(planId);
    try {
      const originUrl = window.location.origin;
      const response = await apiFetch(`${API}/billing/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan_id: planId, origin_url: originUrl })
      });

      let data;
      try { data = await response.json(); } catch { data = {}; }

      if (!response.ok) {
        throw new Error(data.detail || "Erreur lors de la création du paiement");
      }

      if (!data.url) {
        throw new Error("URL Stripe manquante dans la réponse");
      }

      window.location.href = data.url;
    } catch (error) {
      toast.error(error.message);
      setLoadingPlan(null);
    }
  };

  const toggleLang = () => {
    const newLang = isFr ? "en" : "fr";
    navigate(`/pricing?lang=${newLang}`);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#020617]">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-alia.png" style={{height: "80px", objectFit: "contain"}} alt="ALIA Assistant" />
            </Link>

            <div className="flex items-center gap-3">
              <button
                onClick={toggleLang}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                {isFr ? "EN" : "FR"}
              </button>
              <button 
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                {theme === "dark" ? (
                  <Sun className="w-5 h-5 text-slate-400" />
                ) : (
                  <Moon className="w-5 h-5 text-slate-600" />
                )}
              </button>
              {user ? (
                <Link to="/dashboard/inbox">
                  <Button variant="outline" size="sm">{t.dashboard}</Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button variant="outline" size="sm">{t.login}</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Back Link */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-blue-600 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          {t.back}
        </Link>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-blue-600/10 text-blue-600 dark:text-blue-400 border-blue-600/20">
            {t.badge}
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-3" style={{ fontFamily: 'Outfit' }}>
            {t.title}
          </h1>
          <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Test-mode banner — visible only when Stripe is running with a sk_test_ key */}
        {testMode && testCardNumber && (
          <div
            className="max-w-3xl mx-auto mb-8 flex items-start gap-3 p-4 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300"
            data-testid="stripe-test-mode-banner"
          >
            <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed">
              <p className="font-semibold mb-0.5">Mode test Stripe activé</p>
              <p>
                Pour valider un essai sans débit réel, utilisez la carte&nbsp;:{" "}
                <span className="font-mono font-semibold" data-testid="stripe-test-card-number">{testCardNumber}</span>
                {" "}· Date d&apos;expiration future · CVC quelconque (ex.&nbsp;123).
              </p>
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`glass-card relative transition-all hover:shadow-lg ${
                plan.popular ? 'border-blue-500 shadow-lg shadow-blue-500/20 scale-[1.02]' : ''
              }`}
              data-testid={`pricing-plan-${plan.id}`}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4">
                  {t.popular}
                </Badge>
              )}
              <CardContent className="p-6 md:p-8">
                {/* Plan Name */}
                <div className="text-center mb-6">
                  <div className="w-12 h-12 bg-blue-600/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    {plan.id === "business" ? (
                      <Building2 className="w-6 h-6 text-blue-600" />
                    ) : (
                      <Mail className="w-6 h-6 text-blue-600" />
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
                    {plan.name}
                  </h3>
                </div>

                {/* Price */}
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
                      {plan.price}€
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">{t.perMonth}</span>
                  </div>
                  <p className="mt-1 text-sm text-blue-600 dark:text-blue-400 font-medium">
                    {t.trial}
                  </p>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-300">
                      <div className="w-5 h-5 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-green-500" />
                      </div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button 
                  className={`w-full py-5 ${
                    plan.popular 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                  onClick={() => handleStartTrial(plan.id)}
                  disabled={loadingPlan === plan.id}
                  data-testid={`start-trial-${plan.id}`}
                >
                  {loadingPlan === plan.id ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t.ctaLoading}
                    </div>
                  ) : (
                    t.cta
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Payment Note */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 mb-2">
            <CreditCard className="w-4 h-4" />
            <span>{t.cardNote}</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-xs text-slate-400 dark:text-slate-500">
            <Shield className="w-3 h-3" />
            <span>{t.securePayment}</span>
          </div>
        </div>

        {/* Plan Comparator */}
        <div className="max-w-5xl mx-auto mb-16" data-testid="pricing-comparator">
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 dark:text-white mb-6 text-center" style={{ fontFamily: 'Outfit' }}>
            {t.comparatorTitle}
          </h2>
          <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
                  <th className="text-left py-4 px-4 font-medium text-slate-500 dark:text-slate-400 w-2/5">
                    {isFr ? "Fonctionnalité" : "Feature"}
                  </th>
                  <th className="py-4 px-3 text-center font-semibold text-slate-900 dark:text-white">
                    <div className="flex flex-col items-center gap-1">
                      <span>Starter</span>
                      <span className="text-xs font-normal text-slate-500 dark:text-slate-400">39€</span>
                    </div>
                  </th>
                  <th className="py-4 px-3 text-center font-semibold text-blue-600 dark:text-blue-400 bg-blue-500/5">
                    <div className="flex flex-col items-center gap-1">
                      <span>Pro</span>
                      <span className="text-xs font-normal text-slate-500 dark:text-slate-400">99€</span>
                    </div>
                  </th>
                  <th className="py-4 px-3 text-center font-semibold text-slate-900 dark:text-white">
                    <div className="flex flex-col items-center gap-1">
                      <span>Business</span>
                      <span className="text-xs font-normal text-slate-500 dark:text-slate-400">250€</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {t.comparatorFeatures.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100 dark:border-slate-800 last:border-b-0">
                    <td className="py-3 px-4 text-slate-700 dark:text-slate-300">{row.label}</td>
                    {row.values.map((v, j) => (
                      <td
                        key={j}
                        className={`py-3 px-3 text-center ${j === 1 ? 'bg-blue-500/5' : ''}`}
                      >
                        {v === true ? (
                          <Check className="w-4 h-4 text-green-500 inline-block" />
                        ) : v === false ? (
                          <Minus className="w-4 h-4 text-slate-300 dark:text-slate-600 inline-block" />
                        ) : (
                          <span className="text-slate-700 dark:text-slate-200 font-medium text-xs sm:text-sm">{v}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-6 text-center" style={{ fontFamily: 'Outfit' }}>
            {t.faqTitle}
          </h2>
          <div className="space-y-4">
            {t.faq.map((item, i) => (
              <div key={i} className="p-5 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <h4 className="font-medium text-slate-900 dark:text-white mb-2">
                  {item.q}
                </h4>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
